import { computed, ref, watch, onBeforeUnmount, isRef, inject } from '@vue/composition-api'
import useConditionTypes from './useConditionTypes'
import useEffectTypes from './useEffectTypes'
import useFieldCustomValue from './useFieldCustomValue'
import _uniq from 'lodash/uniq'
import _isEqual from 'lodash/isEqual'
import _difference from 'lodash/difference'
import { TOGGLE_MODE_ADD_ONLY, TOGGLE_MODE_REMOVE_ONLY, TOGGLE_MODE_TOGGLE, ADDITIONAL_ACTION_LOGIC } from './fieldUtils'
import { DataError } from '@/utils/errors'
import bugsnagClient from '@/scripts/bugsnag'

export default function useFieldActions ({
  field,
  formValue,
  fieldValue,
  update,
  contextSources = ref({}),
  isReadonly = ref(false),
  actionsDisabled = ref(false),
} = {}) {
  const isReadonlyRef = isRef(isReadonly) ? isReadonly : computed(() => isReadonly)
  const actionsDisabledRef = isRef(actionsDisabled) ? actionsDisabled : computed(() => actionsDisabled)
  const { evaluateCondition } = useConditionTypes()
  const { triggerEffects } = useEffectTypes({
    updateField,
    updateValue: update,
    field,
    isReadonly: isReadonlyRef,
  })
  const { getCustomValue } = useFieldCustomValue()

  const fieldOverrides = ref([])
  const actions = field.value.actions || []
  const sources = computed(() => ({
    form: formValue.value,
    // NOTE: Normalize the contextSources which might be either plain objects or refs
    ...Object.keys(contextSources.value).reduce((prev, curr) => ({
      ...prev,
      [curr]: isRef(contextSources.value[curr]) ? contextSources.value[curr].value : contextSources.value[curr],
    }), {}),
  }))

  // NOTE: Inject a function that can generate additional condition items
  // (which still have to follow the condition type 'allOf' or 'anyOf')
  // WARN: When using this, by default it uses the 'AllOf' condition so all
  // items would need to pass
  const getAdditionalActionLogic = inject(ADDITIONAL_ACTION_LOGIC, () => [])
  const additionalActionLogic = getAdditionalActionLogic(field.value)

  const subscribers = actions.map(action => {
    let loopCounter = 0
    const items = action.logic.items?.length
      ? action.logic.items.concat(additionalActionLogic)
      : [ action.logic ].concat(additionalActionLogic)

    const effects = action.effects
    const watchTargets = items.map(item => computed(() => {
      const logicField = item.field
      // There is a common error on Bugsnag,
      // which is caused by lack of item.field here:
      // https://app.bugsnag.com/coursedog/coursedog-web/errors/6290f7b29406600008654860?filters[event.since]=all&filters[error.status]=open&filters[app.context]=Scheduling
      if (!logicField) {
        const questionId = field.value.questionId
        const error = new DataError(
          `Field (ID: ${questionId}) is missing logic.field (ID: ${item.id})!`
        )
        bugsnagClient.notify(
          error,
          (event) => {
            event.addMetadata('Field', field.value)
          },
        )
        throw error
      }
      const dataSource = sources.value[logicField.source]
      if (!dataSource) {
        throw new Error(
          `Data source "${item.field.source}" (requested by "${item.field.id}" field) ` +
          'is not available in this context. Consider updating contextSources'
        )
      }

      const pathPartials = item.field.id.split('.')
      if (pathPartials.length > 1) {
        const [ rootProp, nestedProp, deeplyNestedProp ] = pathPartials
        const rootPropValue = dataSource[rootProp]

        if (!rootPropValue) return rootPropValue

        if (deeplyNestedProp) {
          return Array.isArray(rootPropValue)
            ? rootPropValue.map(v => v[nestedProp]?.[deeplyNestedProp])
            : rootPropValue[nestedProp]?.[deeplyNestedProp]

        } else {
          return Array.isArray(rootPropValue)
            ? rootPropValue.map(v => v[nestedProp])
            : rootPropValue[nestedProp]
        }
      } else {
        return getCustomValue(item.field.id, dataSource[item.field.id])
      }
    })).concat([ isReadonlyRef, actionsDisabledRef ])

    return watch(watchTargets, evaluateAction, { immediate: true })

    function evaluateAction (sourceValues = [], oldValues = []) {
      // NOTE: return early when actions are disabled or no changes detected
      if (actionsDisabledRef.value) return

      // NOTE: Return early to prevent an inifinite loop
      if (_isEqual(sourceValues, oldValues)) {
        if (loopCounter > 50) return
        loopCounter++
      } else {
        loopCounter = 0
      }

      const isActionSuccessful = action.logic.condition === 'anyOf'
        ? items.some(evaluateCondition(sourceValues, fieldValue.value, formValue.value))
        : items.every(evaluateCondition(sourceValues, fieldValue.value, formValue.value))

      if (isActionSuccessful) triggerEffects(effects, action.logic.id, { sourceValues })
      else removeFieldChanges(action.logic.id)
    }
  })

  function updateField (changes, id) {
    if (fieldOverrides.value.find(override => override.id === id)) return

    fieldOverrides.value.push({
      id,
      changes,
    })
  }

  function removeFieldChanges (id) {
    const indexesToDelete = fieldOverrides.value.map(
      (override, index) => override.id.includes(id)
        ? index
        : -1
    ).filter(i => i > -1)

    fieldOverrides.value = fieldOverrides.value.filter((_, index) => !indexesToDelete.includes(index))
  }

  const fieldValueOverrides = computed(() => fieldOverrides.value
    .filter(override => override.changes.valueOverrides)
    // Remove value overrides when in readonly mode
    .filter(() => !isReadonlyRef.value)
  )

  const updateFieldValueOverrides = (newOverrides = [], oldOverrides = []) => {
    // NOTE: In case there never were any field value overrides
    if (!newOverrides.length && !oldOverrides.length) return

    // NOTE: If value is undefined, fallback to empty
    //       array before updating
    const currentValue = Array.isArray(fieldValue.value)
      ? fieldValue.value
      : []

    const overridesToRemove = oldOverrides
      .filter(oldOverride => !newOverrides.find(newOverride => oldOverride.id === newOverride.id))

    const persistedOverrides = newOverrides
      .filter(newOverride => oldOverrides.find(oldOverride => oldOverride.id === newOverride.id))

    const valuesToKeep = persistedOverrides
      .reduce((values, override) => _uniq(values.concat(override.changes?.valueOverrides?.value || [])), [])

    // NOTE: Only keep overrides that allow for removing the value when effect is off
    const toggleOverridesToRemove = overridesToRemove
      .filter(override => TOGGLE_MODE_TOGGLE === override.changes.valueOverrides.mode)

    const valuesToRemoveIfAlreadyExisting = toggleOverridesToRemove
      .reduce((values, override) => _uniq(values.concat(override.changes?.valueOverrides?.value || [])), [])
      .filter(value => !valuesToKeep.find(valueToKeep => value === valueToKeep))

    // NOTE: Get values that we will remove ALWAYS from the pool of
    //       selected options(this can override additions from other effects)
    const valuesToRemove = newOverrides
      .filter(override => override.changes.valueOverrides.mode === TOGGLE_MODE_REMOVE_ONLY)
      .reduce((values, override) => _uniq(values.concat(override.changes?.valueOverrides?.value || [])), [])

    // NOTE: Values that were removed through Remove Only
    const valuesPreviouslyRemoved = overridesToRemove
      .filter(override => override.changes.valueOverrides.mode === TOGGLE_MODE_REMOVE_ONLY)
      .reduce((values, override) => _uniq(values.concat(override.changes?.valueOverrides?.value || [])), [])

    // NOTE: Values that need to be restored which were
    //       previously removed by Remove Only actions
    const valuesToRestoreIfForceRemoved = valuesToKeep
      .filter(value => valuesPreviouslyRemoved.find(removedValue => value === removedValue))

    const overridesToAdd = newOverrides
      .filter(newOverride => !oldOverrides.find(oldOverride => oldOverride.id === newOverride.id))
      // NOTE: Skip REMOVE_ONLY as its values are "negative" in that presence means removal
      .filter(override => [ TOGGLE_MODE_ADD_ONLY, TOGGLE_MODE_TOGGLE ].includes(override.changes.valueOverrides.mode))

    const getMissingValues = () => {
      if (!valuesToKeep.length) return []
      return valuesToKeep.filter(v => !currentValue.includes(v))
    }

    const valuesToAdd = overridesToAdd
      .reduce((values, override) => _uniq(
        values.concat(override.changes?.valueOverrides?.value || [])
      ), [])
      .concat(valuesToRestoreIfForceRemoved)
      .concat(getMissingValues())

    const newValue = _difference(
      _uniq((currentValue || [])
        .filter(selectedValue => !valuesToRemoveIfAlreadyExisting.find(valueToRemove => valueToRemove === selectedValue))
        .concat(valuesToAdd)
      ),
      valuesToRemove
    )

    update(newValue)
  }

  const unwatchFieldOverrides = watch(fieldValueOverrides, updateFieldValueOverrides)
  onBeforeUnmount(() => {
    unwatchFieldOverrides()
    subscribers.forEach(unwatch => unwatch())
  })

  const fieldWithChanges = computed(() => {
    // NOTE: Return field when actions are disabled
    if (actionsDisabledRef.value) return field.value
    else {
      return fieldOverrides.value
        // NOTE: Remove overrides related to toggling selected options
        .filter(override => !override.changes.valueOverrides)
        .reduce((changes, override) => {
          const mergedConfig = {
            ...changes.config,
            ...(override.changes.config || {}),
          }
          const mergedCustomValidations = (changes.customValidations || []).concat(...(override.changes.customValidations || []))
          return {
            ...changes,
            ...override.changes,
            config: mergedConfig,
            customValidations: mergedCustomValidations,
          }
        }, { ...field.value })
    }
  })

  return {
    fieldWithChanges,
  }
}
